import React from 'react';
import { BookingStatus } from '../../types/venue';
import { theme } from '../../theme';
import { getBookingStatusColor, getBookingStatusText } from '../../utils/venueHelpers';
import { Clock, CheckCircle, XCircle, Ban } from 'lucide-react';

interface BookingStatusBadgeProps {
  status: BookingStatus;
  size?: 'small' | 'medium' | 'large';
}

const BookingStatusBadge: React.FC<BookingStatusBadgeProps> = ({
  status,
  size = 'medium'
}) => {
  const getIcon = (status: BookingStatus) => {
    const iconSize = size === 'small' ? 14 : size === 'large' ? 20 : 16;

    switch (status) {
      case 'PENDING':
        return <Clock size={iconSize} />;
      case 'CONFIRMED':
        return <CheckCircle size={iconSize} />;
      case 'COMPLETED':
        return <CheckCircle size={iconSize} />;
      case 'CANCELLED':
        return <XCircle size={iconSize} />;
      case 'NO_SHOW':
        return <Ban size={iconSize} />;
      default:
        return <Clock size={iconSize} />;
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: '4px 10px',
          fontSize: '0.75rem',
          gap: '4px',
        };
      case 'large':
        return {
          padding: '10px 18px',
          fontSize: '1rem',
          gap: '10px',
        };
      default:
        return {
          padding: '6px 14px',
          fontSize: '0.85rem',
          gap: '6px',
        };
    }
  };

  const sizeStyles = getSizeStyles();
  const statusColor = getBookingStatusColor(status);
  const statusText = getBookingStatusText(status);

  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: sizeStyles.gap,
    backgroundColor: `${statusColor}20`,
    color: statusColor,
    border: `2px solid ${statusColor}`,
    borderRadius: '20px',
    padding: sizeStyles.padding,
    fontSize: sizeStyles.fontSize,
    fontWeight: theme.fonts.weightBold,
    whiteSpace: 'nowrap',
  };

  return (
    <div style={badgeStyle}>
      {getIcon(status)}
      <span>{statusText}</span>
    </div>
  );
};

export default BookingStatusBadge;
